import React, { createContext, type ReactNode, type RefObject, useContext, useMemo } from 'react';

import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';

/**
 * Context type for audio player streaming functionality
 */
export type AudioPlayerStreamingContextType = {
	/** Function to fetch and start audio playback with optional feature override */
	fetchAudio: (featureOverride?: AudioFeatureType) => Promise<void>;
	/** Reference to the HTML audio element */
	audioRef: RefObject<HTMLAudioElement>;
};

/**
 * Context for audio player streaming functionality
 * @private Use useAudioPlayerStreamingContext hook instead of accessing directly
 */
export const AudioPlayerStreamingContext = createContext<AudioPlayerStreamingContextType | null>(null);

/**
 * Props for the AudioPlayerStreamingProvider component
 */
export type AudioPlayerStreamingProviderProps = {
	/** Function to fetch and start audio playback with optional feature override */
	fetchAudio: (featureOverride?: AudioFeatureType) => Promise<void>;
	/** Reference to the HTML audio element */
	audioRef: RefObject<HTMLAudioElement>;
	/** Child components that will have access to the audio streaming context */
	children: ReactNode;
};

/**
 * Provider component that makes audio streaming functionality available to child components
 */
export const AudioPlayerStreamingProvider = ({
	fetchAudio,
	audioRef,
	children,
}: AudioPlayerStreamingProviderProps): React.JSX.Element => {
	const value = useMemo(() => ({ fetchAudio, audioRef }), [fetchAudio, audioRef]);

	return (
		<AudioPlayerStreamingContext.Provider value={value}>
			{children}
		</AudioPlayerStreamingContext.Provider>
	);
};

/**
 * Hook to access audio player streaming context
 * @returns Audio streaming context with fetchAudio function and audioRef
 * @throws Error if used outside of AudioPlayerStreamingProvider
 */
export const useAudioPlayerStreamingContext = (): AudioPlayerStreamingContextType => {
	const context = useContext(AudioPlayerStreamingContext);

	if (!context) {
		throw new Error(
			'useAudioPlayerStreamingContext must be used within an AudioPlayerStreamingProvider'
		);
	}

	return context;
};
